import { alpha, createTheme, Theme } from '@material-ui/core/styles';
import { lightColors, darkColors } from './colors';
import '@fontsource-variable/inter';

export type ThemeMode = 'light' | 'dark';

export const createAppTheme = (
  primaryColor: string,
  secondaryColor: string,
  mode: ThemeMode = 'light'
): Theme => {
  const colors = mode === 'dark' ? darkColors : lightColors;
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      type: isDark ? 'dark' : 'light',
      primary: {
        main: primaryColor,
        light: alpha(primaryColor, isDark ? 0.15 : 0.05),
      },
      secondary: {
        main: secondaryColor,
      },
      background: {
        default: isDark ? '#121212' : '#efefef',
        paper: isDark ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        secondary: isDark ? 'rgba(255, 255, 255, 0.60)' : 'rgba(0, 0, 0, 0.60)',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
      componentBg: isDark ? '#2a2a2a' : '#ffffff',
      grey: {
        50: colors.backgroundLight,
        100: colors.whiteSmoke,
        200: colors.grayLine,
        300: colors.gray88,
        400: colors.gray74,
        500: colors.gray62,
        600: colors.silverSonic,
        700: colors.grayDim,
        800: colors.gray26,
        900: colors.gray13,
      },
      error: {
        main: colors.danger,
        contrastText: isDark ? colors.black : colors.white,
      },
      success: {
        main: colors.success,
        contrastText: isDark ? colors.black : colors.white,
      },
      warning: {
        main: colors.warning,
        contrastText: isDark ? colors.black : colors.white,
      },
      info: {
        main: colors.info,
        contrastText: isDark ? colors.black : colors.white,
      },
    },
    typography: {
      h1: {
        fontSize: 24,
      },
      h2: {
        fontSize: 20,
      },
      h3: {
        fontSize: 16,
      },
      h4: {
        fontSize: 14,
        lineHeight: 'normal',
      },
      h5: {
        fontSize: 12,
      },
      fontFamily: ['"Inter Variable"', '"Roboto"', '"Helvetica"', '"Arial"', 'sans-serif'].join(
        ','
      ),
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            color: colors.black,
            backgroundColor: isDark ? '#121212' : '#efefef',
            transition: 'background-color 0.3s ease, color 0.3s ease',
          },
          '#root': {
            backgroundColor: isDark ? '#121212' : '#efefef',
            minHeight: '100vh',
            transition: 'background-color 0.3s ease',
          },
          '.MuiGrid-container': {
            backgroundColor: isDark ? '#121212' : 'transparent',
          },
          '::-webkit-scrollbar': {
            height: 5,
            width: 5,
          },
          '::-webkit-scrollbar-track': {
            backgroundColor: isDark ? '#2a2a2a' : '#f0f0f0',
          },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? '#555' : '#ccc',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: isDark ? '#666' : '#bbb',
            },
          },
          '::selection': {
            color: isDark ? colors.black : colors.white,
            backgroundColor: primaryColor,
          },
          // Global styles for native HTML inputs to ensure they work in dark mode
          'input, textarea, select': {
            '&:not([class*="Mui"])': {
              backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
              color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              '&::placeholder': {
                color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
              },
            },
          },
          // Force override any remaining white backgrounds in dark mode
          ...(isDark && {
            'div, section, main, article, aside, header, footer': {
              '&[style*="background-color: rgb(255, 255, 255)"], &[style*="backgroundColor: rgb(255, 255, 255)"], &[style*="background-color:#ffffff"], &[style*="background-color: #ffffff"], &[style*="background-color: #FFFFFF"], &[style*="background-color:#FFFFFF"]':
                {
                  backgroundColor: `${isDark ? '#1e1e1e' : '#ffffff'} !important`,
                  color: `${
                    isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)'
                  } !important`,
                },
            },
          }),
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          transition: 'background-color 0.3s ease, color 0.3s ease',
        },
      },
      MuiContainer: {
        root: {
          backgroundColor: isDark ? '#121212' : 'transparent',
          transition: 'background-color 0.3s ease',
        },
      },
      MuiGrid: {
        root: {
          '&.MuiGrid-container': {
            backgroundColor: isDark ? 'transparent' : 'transparent',
          },
        },
        container: {
          backgroundColor: isDark ? 'transparent' : 'transparent',
        },
      },

      MuiAppBar: {
        colorDefault: {
          backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          transition: 'background-color 0.3s ease, color 0.3s ease',
        },
      },
      MuiListItem: {
        root: {
          '&:hover': {
            color: 'unset',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
      MuiButton: {
        root: {
          borderRadius: 5,
          textTransform: 'none',
        },
      },
      MuiDivider: {
        root: {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : colors.black10,
        },
      },
      MuiTypography: {
        h3: {
          color: 'unset',
        },
      },
      MuiListItemText: {
        root: {
          padding: '0 5px',
        },
      },
      MuiInputAdornment: {
        positionStart: {
          marginRight: '4px',
        },
      },
      MuiSelect: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
        },
        select: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
          '&:focus': {
            backgroundColor: isDark ? '#333333' : '#ffffff',
          },
        },
        icon: {
          color: isDark ? 'rgba(255, 255, 255, 0.54)' : 'rgba(0, 0, 0, 0.54)',
        },
      },
      MuiFormLabel: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          '&.Mui-focused': {
            color: primaryColor,
          },
          '&.Mui-error': {
            color: isDark ? '#ff6b6b' : '#f44336',
          },
          '& .MuiFormLabel-asterisk': {
            display: 'none',
          },
        },
      },
      MuiInputLabel: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          '&.Mui-focused': {
            color: primaryColor,
          },
          '&.Mui-error': {
            color: isDark ? '#ff6b6b' : '#f44336',
          },
        },
        outlined: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          '&.Mui-focused': {
            color: primaryColor,
          },
        },
      },
      MuiFormHelperText: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          '&.Mui-error': {
            color: isDark ? '#ff6b6b' : '#f44336',
          },
        },
      },
      MuiMenuItem: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: alpha(primaryColor, isDark ? 0.15 : 0.08),
            color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(primaryColor, isDark ? 0.2 : 0.12),
            color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
      MuiMenu: {
        paper: {
          backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        },
      },
      MuiPopover: {
        paper: {
          backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        },
      },
      MuiAccordionSummary: {
        root: {
          '&.Mui-focused, .Mui-focusVisible': {
            backgroundColor: isDark ? colors.gray10 : colors.gray99,
          },
        },
      },
      MuiFormControlLabel: {
        root: {
          width: 'fit-content',
          marginRight: 0,
        },
        label: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        },
      },
      MuiCheckbox: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          '&.Mui-checked': {
            color: primaryColor,
          },
        },
      },
      MuiRadio: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          '&.Mui-checked': {
            color: primaryColor,
          },
        },
      },
      MuiSwitch: {
        root: {
          '& .MuiSwitch-switchBase': {
            color: isDark ? '#fafafa' : '#fff',
            '&.Mui-checked': {
              color: primaryColor,
              '& + .MuiSwitch-track': {
                backgroundColor: primaryColor,
                opacity: 0.5,
              },
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.38)',
          },
        },
      },
      MuiInputBase: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
          '&.Mui-disabled': {
            color: isDark ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)',
            backgroundColor: isDark ? '#1a1a1a' : '#f5f5f5',
          },
          '&.Mui-focused': {
            backgroundColor: isDark ? '#333333' : '#ffffff',
          },
        },
        input: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          backgroundColor: 'transparent',
          '&::placeholder': {
            color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            opacity: 1,
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
          },
        },
        input: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        },
      },
      MuiFilledInput: {
        root: {
          backgroundColor: isDark ? '#3a3a3a' : 'rgba(0, 0, 0, 0.09)',
          '&:hover': {
            backgroundColor: isDark ? '#404040' : 'rgba(0, 0, 0, 0.13)',
          },
          '&.Mui-focused': {
            backgroundColor: isDark ? '#404040' : 'rgba(0, 0, 0, 0.09)',
          },
        },
        input: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        },
      },
      MuiInput: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          '&:before': {
            borderBottomColor: isDark ? 'rgba(255, 255, 255, 0.42)' : 'rgba(0, 0, 0, 0.42)',
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          },
        },
        input: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        },
      },
      MuiTextField: {
        root: {
          '& .MuiInputLabel-root': {
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: primaryColor,
          },
          '& .MuiFormHelperText-root': {
            color: isDark ? '#ff6b6b' : '#ff6b6b',
          },
        },
      },
      MuiCard: {
        root: {
          backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
          transition: 'background-color 0.3s ease',
        },
      },
      MuiDialog: {
        paper: {
          backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
        },
      },
      MuiAccordion: {
        root: {
          backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          '&:before': {
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiTable: {
        root: {
          backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
        },
      },
      MuiTableCell: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          borderBottomColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)',
        },
        head: {
          backgroundColor: isDark ? '#2a2a2a' : '#fafafa',
          color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          fontWeight: 600,
        },
      },
      MuiTableRow: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: isDark ? '#1a1a1a' : '#fafafa',
          },
        },
      },
      MuiTabs: {
        root: {
          borderBottomColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        },
      },
      MuiTab: {
        root: {
          color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          '&.Mui-selected': {
            color: primaryColor,
          },
          '&:hover': {
            color: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
    },
  });
};

export const theme = (primaryColor: string, secondaryColor: string) =>
  createAppTheme(primaryColor, secondaryColor, 'light');

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    componentBg: string;
  }
  interface PaletteOptions {
    componentBg?: string;
  }
}
